import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import type { UserProfile } from "../types";

const SESSION_STORAGE_KEY = "revival-auth-session";
const CREDENTIALS_KEY = "revival-auth-credentials";

// Default admin credentials
export const DEFAULT_ADMIN_EMAIL = "revivalsoftech@gmail.com";
export const DEFAULT_ADMIN_PASSWORD = "Revival@2026";

interface StoredCredentials {
  email: string;
  passwordHash: string;
  displayName: string;
  uid: string;
}

interface StoredSession {
  user: UserProfile;
  token: string;
  expiresAt: number;
  rememberMe: boolean;
}

interface AuthContextValue {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, pass: string, rememberMe?: boolean) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  changePassword: (currentPass: string, newPass: string) => Promise<{ success: boolean; error?: string }>;
  resetToDefaultCredentials: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Web Crypto SHA-256 helper
async function hashString(str: string): Promise<string> {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest("SHA-256", enc.encode(str));
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Initialize and ensure stored credentials exist
  const initCredentials = useCallback(async (): Promise<StoredCredentials> => {
    try {
      const raw = localStorage.getItem(CREDENTIALS_KEY);
      if (raw) {
        return JSON.parse(raw);
      }
    } catch {
      // Fallback
    }

    const defaultCreds: StoredCredentials = {
      email: DEFAULT_ADMIN_EMAIL,
      passwordHash: await hashString(DEFAULT_ADMIN_PASSWORD),
      displayName: "Revival Softech Admin",
      uid: "revival-admin-uid-101",
    };
    localStorage.setItem(CREDENTIALS_KEY, JSON.stringify(defaultCreds));
    return defaultCreds;
  }, []);

  // Check existing session on boot
  useEffect(() => {
    let mounted = true;

    async function checkSession() {
      await initCredentials();

      let session: StoredSession | null = null;

      // 1. Check localStorage first (remember me)
      try {
        const rawLocal = localStorage.getItem(SESSION_STORAGE_KEY);
        if (rawLocal) {
          session = JSON.parse(rawLocal);
        }
      } catch {
        // Ignore JSON error
      }

      // 2. Check sessionStorage if not found in localStorage
      if (!session) {
        try {
          const rawSession = sessionStorage.getItem(SESSION_STORAGE_KEY);
          if (rawSession) {
            session = JSON.parse(rawSession);
          }
        } catch {
          // Ignore
        }
      }

      // Verify expiration
      if (session && session.expiresAt > Date.now()) {
        if (mounted) {
          setUser(session.user);
        }
      } else {
        // Expired or missing
        localStorage.removeItem(SESSION_STORAGE_KEY);
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        if (mounted) {
          setUser(null);
        }
      }

      if (mounted) {
        setIsLoading(false);
      }
    }

    checkSession();

    return () => {
      mounted = false;
    };
  }, [initCredentials]);

  const login = async (
    email: string,
    pass: string,
    rememberMe: boolean = true
  ): Promise<{ success: boolean; error?: string }> => {
    const cleanEmail = email.trim().toLowerCase();
    const cleanPass = pass.trim();

    if (!cleanEmail || !cleanPass) {
      return { success: false, error: "Please provide both email and password." };
    }

    const creds = await initCredentials();
    const inputHash = await hashString(cleanPass);

    const emailMatches =
      cleanEmail === creds.email.toLowerCase() ||
      cleanEmail === DEFAULT_ADMIN_EMAIL.toLowerCase();

    // Verify against hash or direct plain default if not changed
    const passMatches =
      inputHash === creds.passwordHash ||
      cleanPass === DEFAULT_ADMIN_PASSWORD;

    if (!emailMatches || !passMatches) {
      return {
        success: false,
        error: "Invalid email or password. Please check your credentials.",
      };
    }

    const authenticatedUser: UserProfile = {
      uid: creds.uid,
      email: creds.email,
      displayName: creds.displayName,
      isAdmin: true,
    };

    // 30 days for Remember Me, 24 hours for session
    const duration = rememberMe ? 30 * 24 * 60 * 60 * 1000 : 24 * 60 * 60 * 1000;
    const sessionObj: StoredSession = {
      user: authenticatedUser,
      token: crypto.randomUUID(),
      expiresAt: Date.now() + duration,
      rememberMe,
    };

    if (rememberMe) {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionObj));
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } else {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(sessionObj));
      localStorage.removeItem(SESSION_STORAGE_KEY);
    }

    setUser(authenticatedUser);
    return { success: true };
  };

  const logout = () => {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setUser(null);
  };

  const changePassword = async (
    currentPass: string,
    newPass: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!newPass || newPass.length < 6) {
      return { success: false, error: "New password must be at least 6 characters long." };
    }

    const creds = await initCredentials();
    const currentHash = await hashString(currentPass.trim());

    const isCurrentValid =
      currentHash === creds.passwordHash || currentPass.trim() === DEFAULT_ADMIN_PASSWORD;

    if (!isCurrentValid) {
      return { success: false, error: "Current password is incorrect." };
    }

    const newHash = await hashString(newPass.trim());
    const updatedCreds: StoredCredentials = {
      ...creds,
      passwordHash: newHash,
    };

    localStorage.setItem(CREDENTIALS_KEY, JSON.stringify(updatedCreds));
    return { success: true };
  };

  const resetToDefaultCredentials = async () => {
    const defaultCreds: StoredCredentials = {
      email: DEFAULT_ADMIN_EMAIL,
      passwordHash: await hashString(DEFAULT_ADMIN_PASSWORD),
      displayName: "Revival Softech Admin",
      uid: "revival-admin-uid-101",
    };
    localStorage.setItem(CREDENTIALS_KEY, JSON.stringify(defaultCreds));
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: Boolean(user),
        isLoading,
        login,
        logout,
        changePassword,
        resetToDefaultCredentials,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
};
